/**
 * When a calendar event has attendees (RSVP), check for declined responses.
 * On decline: mark player unavailable for that date and auto-reschedule match to next slot.
 * Calendar event is then updated when owner next syncs (match has new time).
 */

import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getCalendarEvent } from './googleCalendarService';
import { addExclusionDateForPlayer } from './playerAvailabilityService';
import { rescheduleMatchToNextAvailableSlot } from './singleMatchRescheduleService';
import { toUtcMs } from '../utils/dateTimeUtils';

function toDateKey(dateLike) {
  const ms = toUtcMs(dateLike);
  if (ms == null || isNaN(ms)) return '';
  const d = new Date(ms);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Find userId for an email (first match in users collection).
 */
async function getUserIdByEmail(email) {
  if (!email || typeof email !== 'string') return null;
  const q = query(collection(db, 'users'), where('email', '==', email.trim()), where('email', '!=', ''));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return snap.docs[0].id;
}

/**
 * Check a match's calendar event (with attendees) for declined RSVPs.
 * If any attendee declined: add that date to their exclusion, reschedule match to next available slot.
 * Call when the calendar event owner syncs (e.g. after updateMatchEvent).
 * @param {object} match - Match with calendarEventId, tournamentId, scheduledDate
 * @param {string} accessToken - Owner's Google OAuth token (to fetch event)
 * @returns {Promise<{ rescheduled: boolean, declinedUserIds: string[], error?: string }>}
 */
export async function checkCalendarDeclinesAndReschedule(match, accessToken) {
  const eventId = match.calendarEventId;
  const tournamentId = match.tournamentId;
  if (!eventId || !tournamentId) {
    return { rescheduled: false, declinedUserIds: [] };
  }

  const event = await getCalendarEvent(accessToken, eventId);
  if (!event || !event.attendees || event.attendees.length === 0) {
    return { rescheduled: false, declinedUserIds: [] };
  }

  const declinedEmails = (event.attendees || []).filter(
    (a) => (a.responseStatus || '').toLowerCase() === 'declined'
  ).map((a) => a.email);
  if (declinedEmails.length === 0) {
    return { rescheduled: false, declinedUserIds: [] };
  }

  const dateKey = toDateKey(match.scheduledDate ?? match.scheduledAt);
  const declinedUserIds = [];
  for (const email of declinedEmails) {
    const uid = await getUserIdByEmail(email);
    if (uid) {
      declinedUserIds.push(uid);
      await addExclusionDateForPlayer(tournamentId, uid, dateKey, match.id);
    }
  }

  const result = await rescheduleMatchToNextAvailableSlot(match.id);
  return {
    rescheduled: result.rescheduled === true,
    declinedUserIds,
    newDate: result.newDate,
    newTime: result.newTime,
    error: result.error,
  };
}
